import { Trans } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { ProfileEditForm, type ProfileFormData } from "@/components/account/profile-edit-form";
import { Skeleton } from "@/components/ui/skeleton";

interface SettingsData {
  profileData: ProfileFormData;
}

const SETTINGS_KEY = ["api", "/api/account/user/settings-data"] as const;

/**
 * Standalone /account/user/settings/profile page (legacy parity). Renders
 * the same `ProfileEditForm` used by the settings hub's profile tab.
 */
export const Route = createFileRoute("/_authenticated/account/user/settings/profile")({
  component: ProfileSettingsPage,
});

function ProfileSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => apiFetch.get<SettingsData>("/api/account/user/settings-data"),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
          <Trans>Profile</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Personal details, photo, language, and theme.</Trans>
        </p>
      </header>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : isError || !data ? (
        <div className="border-destructive/40 bg-destructive/5 rounded-xl border p-6 text-center">
          <p className="text-destructive text-sm font-medium">
            <Trans>Could not load profile.</Trans>
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 mt-4 inline-flex h-9 items-center justify-center rounded-lg px-4 text-sm font-medium transition-colors"
          >
            <Trans>Retry</Trans>
          </button>
        </div>
      ) : (
        <ProfileEditForm
          initialData={data.profileData}
          onSaved={() => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY })}
        />
      )}
    </div>
  );
}
